import AddCommentIcon from "@mui/icons-material/AddComment";
import Alert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import CommentIcon from "@mui/icons-material/Comment";
import DoneIcon from "@mui/icons-material/Done";
import ReplayIcon from "@mui/icons-material/Replay";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import { skipToken } from "@reduxjs/toolkit/query";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha, styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useInfiniteScroll } from "_shared/_hooks/infinite-scroll/use-infinite-scroll";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import ProposalDeleteActionButton from "work/proposals/_views/_shared/proposal-delete-action-button";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import React, { ChangeEvent, useCallback, useEffect, useId, useMemo } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import UserCoworkerQuery, {
  UserCoworkerFilterParameters,
  UserCoworkerOrderField,
  UserCoworkerOrderParameters,
} from "users/information/coworker-information/_api/commands/query-user-coworker-api-request";
import useInfiniteUserCoworkers from "users/information/coworker-information/_hooks/use-infinite-user-coworkers";
import { getUserCoworkersByUserIds } from "users/information/coworker-information/_stores/user-coworkers-redux-slice";
import UserCoworker from "users/information/coworker-information/user-coworker";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import { getUpdatedProposalBuilderByProposalId } from "work/proposals/_store/proposals-redux-slice";
import {
  clearStagedReviewerSelection,
  proposalUncommittedInputScopeKey,
  readStagedReviewerSelection,
  writeStagedReviewerSelection,
} from "work/proposals/_store/proposal-uncommitted-input-store";
import Proposal from "work/proposals/proposal";
import ReviewerMessagePopover from "work/proposals/reviewers/_views/reviewer-message-popover";
import ProposalReviewer from "../proposal-reviewer";
import {
  getReviewerSendActionState,
  orderReviewersForDisplay,
  removeReviewer,
  toggleReviewerEditing,
  toggleReviewerSelection,
} from "work/proposals/reviewers/_views/reviewer-selection";
import LoadingButton from "_shared/_views/loading/loading-button";

const PopoverContainer = styled("div")(({ theme }) => ({
  boxSizing: "border-box",
  maxWidth: `calc(100vw - ${theme.spacing(4)})`,
  minWidth: 0,
  padding: theme.spacing(2),
  textAlign: "center",
  width: "25rem",
  [theme.breakpoints.down("sm")]: {
    maxWidth: `calc(100vw - ${theme.spacing(2)})`,
  },
}));
const ReviewerPopover = styled(Popover)(({ theme }) => ({
  "& .MuiPopover-paper": {
    maxWidth: `calc(100vw - ${theme.spacing(2)})`,
    [theme.breakpoints.up("sm")]: {
      maxWidth: `calc(100vw - ${theme.spacing(4)})`,
    },
  },
}));
const SearchField = styled(TextField)(() => ({
  width: "100%",
})) as typeof TextField;
const ReviewerNotice = styled(Alert)(({ theme }) => ({
  marginTop: theme.spacing(1),
  textAlign: "left",
}));
const LoaderProgress = styled(CircularProgress)(({ theme }) => ({
  display: "inline-block",
  marginBottom: theme.spacing(4),
  marginTop: theme.spacing(4),
}));
const ListContainer = styled(List)(() => ({
  maxHeight: "20rem",
  overflowX: "hidden",
  overflowY: "auto",
}));
const SelectedIcon = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  opacity: "0.75",
  position: "absolute",
  zIndex: 1,
}));
const EditingSwitch = styled(FormControlLabel)(() => ({
  alignItems: "center",
  margin: 0,
  width: "4.75rem",
  "& .MuiFormControlLabel-label": {
    fontSize: "0.7em",
    lineHeight: 1.1,
    whiteSpace: "nowrap",
  },
}));
const ReviewerAvatar = styled(ListItemAvatar)(() => ({
  flexShrink: 0,
  position: "relative",
}));
const ReviewerListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ selected, theme }) => ({
  alignItems: "stretch",
  backgroundColor: selected ? alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity) : "transparent",
  display: "flex",
  padding: 0,
  "&:hover": {
    backgroundColor: selected
      ? alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity)
      : theme.palette.action.hover,
  },
  "& .MuiListItemButton-root": {
    backgroundColor: "transparent",
    flex: "1 1 auto",
    minWidth: 0,
    paddingRight: theme.spacing(1),
    "&.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: "transparent",
    },
  },
}));
const ReviewerListItemText = styled(ListItemText, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ selected, theme }) => ({
  minWidth: 0,
  "& .MuiListItemText-primary": {
    color: selected ? theme.palette.primary.main : "initial",
    fontWeight: selected ? theme.typography.fontWeightBold : theme.typography.fontWeightRegular,
  },
  "& .MuiListItemText-secondary": {
    color: theme.palette.text.secondary,
  },
}));
/**
 * A fixed three-column tray: editing switch, message action, removal.
 *
 * Fixed, and always all three, because the columns used to appear only when they applied. A selected
 * reviewer carried two more controls than an unselected one, so its actions tray was wider, the row
 * button beside it correspondingly narrower, and the editing switch of a selected reviewer sat a
 * hundred-odd pixels to the left of every other switch in the list. Reserving the columns on every
 * row is what makes the switches line up, and an empty cell costs nothing to look at.
 */
const ActionsContainer = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "grid",
  flex: "0 0 auto",
  gap: theme.spacing(0.5),
  gridTemplateColumns: "4.75rem 2.25rem 2.25rem",
  justifyItems: "center",
  padding: theme.spacing(0.75, 1, 0.75, 0),
}));
/**
 * Icon only, for both the message action and the removal.
 *
 * Reserving the columns above only aligns the rows; it does not pay for them. The captions these
 * two carried ("add message", "re-review") cost about 2.75rem of a 25rem popover, which is what
 * wrapped a selected reviewer's name onto a second line while their neighbours' names fitted. The
 * tooltip and the aria-label already say what each control does, so the caption was the one part of
 * it nobody was reading (#5134, #5186 review).
 */
const ReviewerIconAction = styled(Stack)(() => ({
  alignItems: "center",
  justifyContent: "center",
  minWidth: 0,
}));
const ReviewerLoadingListItem = styled(ListItem)(() => ({
  justifyContent: "center",
}));
const ReviewerLoadError = styled(Alert)(() => ({
  textAlign: "left",
  width: "100%",
}));
const ReviewerSecondary = styled("span")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(0.25),
  minWidth: 0,
}));
const ReviewerStatus = styled("span", {
  shouldForwardProp: (prop) => prop !== "approved",
})<{ approved?: boolean }>(({ approved, theme }) => ({
  color: approved ? theme.palette.success.main : theme.palette.text.secondary,
}));
const ReviewerMessage = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  display: "block",
  fontSize: ".9em",
  fontStyle: "italic",
  lineHeight: 1.25,
  maxWidth: "100%",
  overflow: "hidden",
  padding: 0,
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const ReviewerMessageTooltipTitle = styled("span")(() => ({
  whiteSpace: "pre-wrap",
}));
const SendActionContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(0.5),
  justifyItems: "center",
  paddingTop: theme.spacing(1.5),
}));
const SendActionDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  textAlign: "center",
}));
const EMPTY_USER_COWORKERS: UserCoworker[] = [];

type ReviewerSelectorProps = {
  proposal?: Proposal;
  popoverAnchor?: HTMLButtonElement;
  reviewerSource?: "proposal" | "proposalBuilder";
  reviewerBaseline?: ProposalReviewer[];
  onReviewersChanged?: (reviewers: ProposalReviewer[]) => void;
  onPopoverClose?: () => void;
  /**
   * Whether this actor may ask a reviewer to review again. The same capability that governs
   * assigning reviewers (`canManageReviewers`), and false whenever the proposal has no
   * collaboration envelope -- a legacy proposal has no event stream to record the request in.
   */
  canRequestReReview?: boolean;
  /** Fires immediately on click; the caller issues the command. */
  onRequestReReview?: (reviewer: ProposalReviewer) => void;
  /** Reviewer user IDs with a re-review request in flight, so their button can show progress. */
  reReviewPendingUserIds?: ReadonlySet<string>;
  /**
   * Persists the staged roster from inside this popover (#4842), which the page header's Save cannot do
   * while the popover is open. Omit it and no Send button renders. `confirmSave` says whether the press
   * is carrying unsaved proposal content that the actor should be asked about first (#5134).
   */
  onSendToReviewers?: (args: { confirmSave: boolean }) => void;
  /**
   * The host page's unsaved-changes signal — its `isDirty`. It no longer gates Send (#5134); it decides
   * whether Send confirms before it saves.
   */
  proposalHasUnsavedChanges?: boolean;
  /** True while that save is in flight, so Send shows progress and cannot be pressed twice. */
  isSendingToReviewers?: boolean;
};

function getReviewerWithCoworkerIdentity(
  reviewer: ProposalReviewer,
  reviewerCoworkers: UserCoworker[],
): ProposalReviewer {
  const coworker = reviewerCoworkers.find((currentCoworker) => currentCoworker.userId.isEqualTo(reviewer.userId));

  return coworker ? ProposalReviewer.createReviewerFromCoworker(coworker, reviewer) : reviewer.clone();
}

function normalizeReviewersForSelector(
  reviewers: ProposalReviewer[],
  reviewerCoworkers: UserCoworker[],
): ProposalReviewer[] {
  return reviewers.map((reviewer) => getReviewerWithCoworkerIdentity(reviewer, reviewerCoworkers));
}

function getEligibleReviewerCoworkers(coworkers: UserCoworker[], currentUserId?: string): UserCoworker[] {
  return coworkers.filter((coworker) => coworker.userId.value !== currentUserId);
}

function mergeCoworkersByUserId(...coworkerGroups: UserCoworker[][]): UserCoworker[] {
  const coworkersByUserId = new Map<string, UserCoworker>();
  coworkerGroups.flat().forEach((coworker) => coworkersByUserId.set(coworker.userId.value, coworker));
  return [...coworkersByUserId.values()];
}

function findExistingReviewer(reviewers: ProposalReviewer[], coworker: UserCoworker): ProposalReviewer | undefined {
  return reviewers.find((reviewer) => reviewer.userId.isEqualTo(coworker.userId));
}

function createReviewerPool(coworkers: UserCoworker[], proposalReviewers: ProposalReviewer[]): ProposalReviewer[] {
  return coworkers.map((coworker) =>
    ProposalReviewer.createReviewerFromCoworker(coworker, findExistingReviewer(proposalReviewers, coworker)),
  );
}

function filterReviewerPool(
  reviewerPool: ProposalReviewer[],
  reviewerCoworkers: UserCoworker[],
  searchTerm: string,
): ProposalReviewer[] {
  if (searchTerm === "") return reviewerPool;

  const normalizedSearchTerm = searchTerm.toLowerCase();
  return reviewerPool.filter((reviewer) => {
    const coworker = reviewerCoworkers.find((currentCoworker) =>
      currentCoworker.userId.isEqualTo(reviewer.userId),
    );

    return (
      coworker?.name.fullName.toLowerCase().includes(normalizedSearchTerm) === true ||
      coworker?.email.toString().toLowerCase().includes(normalizedSearchTerm) === true
    );
  });
}

function mergeReviewersByUserId(...reviewerGroups: ProposalReviewer[][]): ProposalReviewer[] {
  const reviewersByUserId = new Map<string, ProposalReviewer>();

  reviewerGroups.flat().forEach((reviewer) => {
    if (!reviewersByUserId.has(reviewer.userId.value)) {
      reviewersByUserId.set(reviewer.userId.value, reviewer);
    }
  });

  return Array.from(reviewersByUserId.values());
}

/**
 * The single authoritative baseline this selector renders from when there is no in-progress local
 * change to show instead: the persisted (server-acknowledged) reviewer for anyone persisted already
 * knows about, and the builder's own entry for a reviewer that exists only there — a reviewer picked
 * on a fresh draft before its first save, for instance, which persisted has no way to know about yet.
 *
 * Persisted wins whenever both sources know the same reviewer. This is what makes a reviewer's
 * already-granted access render correctly the moment the popover opens even when the local draft
 * builder has not rebased onto that grant (see the "builder trails" landmine below) — the builder is
 * never trusted over persisted for a reviewer both sides already carry.
 */
function mergeReviewerBaseline(
  persistedReviewers: ProposalReviewer[],
  builderReviewers: ProposalReviewer[],
): ProposalReviewer[] {
  const persistedUserIds = new Set(persistedReviewers.map((reviewer) => reviewer.userId.value));
  const builderOnlyReviewers = builderReviewers.filter((reviewer) => !persistedUserIds.has(reviewer.userId.value));

  return [...persistedReviewers, ...builderOnlyReviewers];
}

/**
 * True when the persisted baseline has become exactly the selection this editing session staged —
 * that is, the save carrying the staged change was acknowledged by the server. Checked against
 * persisted alone: the local draft builder is not authoritative (it can trail an acknowledged grant
 * indefinitely — see `mergeReviewerBaseline`), so requiring it to agree too would leave a genuinely
 * acknowledged change staged forever whenever the builder never catches up.
 *
 * Only the three dimensions this selector can change are compared: membership, edit rights, and the
 * custom message. `dateApproved` is server-owned (reviewer access changes deliberately do not reset an
 * endorsement), so including it would make an acknowledgement unrecognizable.
 */
function reviewerSelectionIsAcknowledged(
  persistedReviewers: ProposalReviewer[],
  stagedReviewers: ProposalReviewer[],
): boolean {
  if (persistedReviewers.length !== stagedReviewers.length) return false;

  return stagedReviewers.every((staged) => {
    const persisted = persistedReviewers.find((reviewer) => reviewer.userId.isEqualTo(staged.userId));

    return (
      persisted !== undefined &&
      persisted.isEditing === staged.isEditing &&
      (persisted.customMessage ?? "") === (staged.customMessage ?? "")
    );
  });
}

export default function ReviewerSelector(props: Readonly<ReviewerSelectorProps>) {
  const { proposal, popoverAnchor, reviewerBaseline, onReviewersChanged, onPopoverClose } = props;
  const canRequestReReview = props.canRequestReReview ?? false;
  const onRequestReReview = props.onRequestReReview;
  const reReviewPendingUserIds = props.reReviewPendingUserIds;
  const onSendToReviewers = props.onSendToReviewers;
  const proposalHasUnsavedChanges = props.proposalHasUnsavedChanges ?? false;
  const isSendingToReviewers = props.isSendingToReviewers ?? false;
  const sendActionDescriptionId = useId();
  const reviewerSource = props.reviewerSource ?? "proposalBuilder";
  const session = useSession();
  const theme = useTheme();
  const isMobileDisplay = useMediaQuery(theme.breakpoints.down("md"));
  const reviewerListRef = React.useRef<HTMLUListElement>(null);

  const updatedBuilderJSON = useSelector(getUpdatedProposalBuilderByProposalId(proposal?.id ?? Guid.EMPTY));
  const updatedBuilder = useMemo(() => {
    return ProposalBuilder.fromJSON({
      json: updatedBuilderJSON,
      session,
    });
  }, [session, updatedBuilderJSON]);

  const [currentReviewerEditing, setCurrentReviewerEditing] = React.useState<ProposalReviewer>();
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState<string>("");
  const [popoverAnchorElement, setPopoverAnchorElement] = React.useState<HTMLElement | null>(null);
  const popoverWasOpen = React.useRef(false);
  // The one piece of state this selector owns: the whole desired reviewer selection this session has
  // explicitly staged (via a click/toggle/message edit), or undefined when nothing is staged and the
  // rendered rows should simply reflect the authoritative baseline below. It survives every popover
  // close/reopen — cleared only by the acknowledgement block further down, never by a re-render or a
  // reopen — so a just-made toggle can never revert on its own (see git history for the bug this
  // replaced: an unconditional reset-on-reopen that discarded a staged change the builder had not yet
  // echoed).
  //
  // S4: it is also mirrored to the durable uncommitted-input store (keyed by proposal id, same durable
  // home as the comment composer draft) so a proposal-page remount or a full reload restores it, not
  // just a popover close/reopen. Lazily seeded from that store on mount so a remounted selector starts
  // from whatever was last staged rather than from nothing.
  const uncommittedInputScopeKey = proposalUncommittedInputScopeKey(session);
  const [stagedReviewerSelection, setStagedReviewerSelectionState] = React.useState<ProposalReviewer[] | undefined>(
    () => readStagedReviewerSelection(uncommittedInputScopeKey, proposal?.id),
  );
  const setStagedReviewerSelection = useCallback(
    (reviewers: ProposalReviewer[] | undefined) => {
      setStagedReviewerSelectionState(reviewers);
      if (reviewers === undefined) clearStagedReviewerSelection(uncommittedInputScopeKey, proposal?.id);
      else writeStagedReviewerSelection(uncommittedInputScopeKey, proposal?.id, reviewers);
    },
    [uncommittedInputScopeKey, proposal?.id],
  );
  const builderReviewers = useMemo(() => {
    if (!popoverAnchor) return [];
    const currentProposalReviewers = session.context?.viewingAsVendor
      ? proposal?.vendorReviewers
      : proposal?.clientReviewers;
    if (reviewerSource === "proposal") return currentProposalReviewers ?? [];

    return session.context?.viewingAsVendor
      ? (updatedBuilder?.currentSpec.vendorReviewers?.map((r) => new ProposalReviewer(r)) ??
          currentProposalReviewers ??
          [])
      : (updatedBuilder?.currentSpec.clientReviewers?.map((r) => new ProposalReviewer(r)) ??
          currentProposalReviewers ??
          []);
  }, [
    session.context?.viewingAsVendor,
    popoverAnchor,
    reviewerSource,
    updatedBuilder?.currentSpec.vendorReviewers,
    updatedBuilder?.currentSpec.clientReviewers,
    proposal?.vendorReviewers,
    proposal?.clientReviewers,
  ]);
  const persistedReviewers = useMemo(() => {
    if (!popoverAnchor) return [];

    const currentProposalReviewers = session.context?.viewingAsVendor
      ? proposal?.vendorReviewers
      : proposal?.clientReviewers;
    return reviewerBaseline ?? currentProposalReviewers ?? [];
  }, [
    session.context?.viewingAsVendor,
    popoverAnchor,
    reviewerBaseline,
    proposal?.vendorReviewers,
    proposal?.clientReviewers,
  ]);
  const reviewerIdentityUserIds = useMemo(() => {
    const userIds = new Set<string>();
    [...builderReviewers, ...persistedReviewers].forEach((reviewer) => userIds.add(reviewer.userId.value));

    return userIds;
  }, [builderReviewers, persistedReviewers]);
  const reviewerIndividualsQuery = useGetIndividualsByUserIdsQuery(
    reviewerIdentityUserIds.size ? { userIds: reviewerIdentityUserIds } : skipToken,
  );
  const reviewerIndividuals = useMemo(
    () => Individual.fromJSONArray(reviewerIndividualsQuery.data, { allowEmpty: true }) ?? [],
    [reviewerIndividualsQuery.data],
  );
  const coworkerQuery = useMemo(
    () =>
      new UserCoworkerQuery(
        new UserCoworkerFilterParameters(),
        new UserCoworkerOrderParameters(UserCoworkerOrderField.Name, OrderDirection.Ascending),
        PaginationParameters.DEFAULT,
        debouncedSearchTerm.trim() || undefined,
      ),
    [debouncedSearchTerm],
  );
  const {
    coworkers,
    isLoading,
    isInitialLoading,
    error: coworkerError,
    loadMore: loadMoreCoworkers,
    retry: retryCoworkers,
  } = useInfiniteUserCoworkers(coworkerQuery, { skip: popoverAnchor === undefined });
  const reviewerPoolCoworkers = useMemo(
    () => getEligibleReviewerCoworkers(coworkers.data, session.user?.id?.value),
    [coworkers.data, session.user?.id?.value],
  );
  const storedBaselineReviewerCoworkers =
    useSelector(getUserCoworkersByUserIds(reviewerIdentityUserIds)) ?? EMPTY_USER_COWORKERS;
  const reviewerNormalizationCoworkers = useMemo(
    () => mergeCoworkersByUserId(reviewerPoolCoworkers, storedBaselineReviewerCoworkers),
    [reviewerPoolCoworkers, storedBaselineReviewerCoworkers],
  );
  // `originalReviewers` is the pure persisted baseline (never merged with the builder) — it is the
  // diff target the "added"/"removed" badges compare against, so it must stay exactly what the server
  // last acknowledged. `selectedReviewers` is what actually renders: the staged selection while one is
  // in progress, otherwise the merged baseline (persisted, filled out with any builder-only reviewer —
  // see `mergeReviewerBaseline`). Both are plain derivations of props/state, recomputed every render;
  // nothing here needs an effect to "reseed" on open/close, which removes the whole class of bug where
  // a reopen raced a source that had not yet caught up.
  const originalReviewers = useMemo(
    () => normalizeReviewersForSelector(persistedReviewers, reviewerNormalizationCoworkers),
    [persistedReviewers, reviewerNormalizationCoworkers],
  );
  const mergedBaselineReviewers = useMemo(() => {
    const normalizedBuilderReviewers = normalizeReviewersForSelector(builderReviewers, reviewerNormalizationCoworkers);
    return mergeReviewerBaseline(originalReviewers, normalizedBuilderReviewers);
  }, [builderReviewers, originalReviewers, reviewerNormalizationCoworkers]);
  const selectedReviewers = stagedReviewerSelection ?? mergedBaselineReviewers;
  const selectedReviewerUserIds = useMemo(
    () => new Set([...selectedReviewers, ...originalReviewers].map((reviewer) => reviewer.userId.value)),
    [originalReviewers, selectedReviewers],
  );
  const storedSelectedReviewerCoworkers =
    useSelector(getUserCoworkersByUserIds(selectedReviewerUserIds)) ?? EMPTY_USER_COWORKERS;
  const reviewerDisplayCoworkers = useMemo(
    () => mergeCoworkersByUserId(reviewerNormalizationCoworkers, storedSelectedReviewerCoworkers),
    [reviewerNormalizationCoworkers, storedSelectedReviewerCoworkers],
  );
  // Seeded from the merged baseline rather than the builder alone, so a coworker row opens carrying the
  // access the server has already acknowledged even when the local draft builder still trails it.
  const reviewerPool = useMemo(
    () => createReviewerPool(reviewerPoolCoworkers, mergedBaselineReviewers.map((reviewer) => reviewer.clone())),
    [mergedBaselineReviewers, reviewerPoolCoworkers],
  );

  useInfiniteScroll(loadMoreCoworkers, coworkers.hasNextPage, isLoading, reviewerListRef);

  // Presentation-only: clear the search box each time the popover is freshly opened. Selection state
  // is deliberately untouched here — see the acknowledgement block below for the only thing that ever
  // clears a staged reviewer change.
  useEffect(() => {
    const popoverIsOpen = popoverAnchor !== undefined;
    const popoverJustOpened = popoverIsOpen && !popoverWasOpen.current;
    popoverWasOpen.current = popoverIsOpen;

    if (popoverJustOpened) {
      setSearchTerm("");
      setDebouncedSearchTerm("");
    }
  }, [popoverAnchor]);

  // The only place a staged reviewer selection is ever cleared: the persisted baseline has become
  // exactly what this session staged, i.e. the save carrying it was acknowledged. This does not wait
  // on the local draft builder too — see `reviewerSelectionIsAcknowledged` — so a staged change
  // survives every close/reopen and every unrelated re-render (a counterparty edit, a realtime refresh
  // that races the save) until the server actually agrees with it, and never reverts on its own.
  //
  // Adjusted during render rather than in an effect (React's documented pattern for reacting to a prop
  // change without an extra render pass — see "you might not need an effect"): the check only needs to
  // run when `persistedReviewers` itself has actually changed, which a ref comparison detects without
  // the cascading-render an effect+setState here would cause.
  //
  // Gated on the popover being open: `persistedReviewers` is deliberately emptied while closed (see its
  // own memo above), and that collapse is itself a "persistedReviewers changed" transition — comparing
  // a staged selection against that empty stand-in, rather than simply not looking, could clear it for
  // the wrong reason (an empty staged selection would read as "acknowledged" against an empty array on
  // every close, whether or not the server agrees with it).
  const previouslyObservedPersistedReviewers = React.useRef(persistedReviewers);
  if (persistedReviewers !== previouslyObservedPersistedReviewers.current) {
    previouslyObservedPersistedReviewers.current = persistedReviewers;
    if (
      stagedReviewerSelection &&
      popoverAnchor !== undefined &&
      reviewerSelectionIsAcknowledged(persistedReviewers, stagedReviewerSelection)
    ) {
      setStagedReviewerSelection(undefined);
    }
  }

  useEffect(() => {
    const timeoutId = globalThis.setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 300);
    return () => globalThis.clearTimeout(timeoutId);
  }, [searchTerm]);

  function handlePopoverClosed() {
    onPopoverClose?.();
  }

  function search(value: string) {
    setSearchTerm(value);

    if (value === "") {
      setDebouncedSearchTerm("");
    }
  }

  function handleReviewerSelected(event: React.MouseEvent<HTMLDivElement>, reviewer: ProposalReviewer) {
    event.stopPropagation();

    const updatedReviewers = toggleReviewerSelection({
      selectedReviewers,
      reviewer,
    });

    setStagedReviewerSelection(updatedReviewers);
    onReviewersChanged?.(updatedReviewers);
  }

  function handleReviewerRemoved(event: React.MouseEvent<HTMLButtonElement, MouseEvent>, reviewer: ProposalReviewer) {
    event.stopPropagation();

    const updatedReviewers = removeReviewer({
      selectedReviewers,
      reviewer,
    });

    setStagedReviewerSelection(updatedReviewers);
    onReviewersChanged?.(updatedReviewers);
  }

  function handleEditToggled(_: ChangeEvent<HTMLInputElement>, editedReviewer: ProposalReviewer) {
    const updatedReviewers = toggleReviewerEditing({
      selectedReviewers,
      reviewer: editedReviewer,
    });

    setStagedReviewerSelection(updatedReviewers);
    onReviewersChanged?.(updatedReviewers);
  }

  // Deliberately NOT staged behind the popover's Save, unlike every roster edit above it. A
  // re-review request changes no proposal content -- it dismisses one reviewer's approval -- so
  // there is nothing to stage alongside the roster diff, and holding it back would leave the
  // leader looking at a button that appeared to do nothing.
  function handleReReviewRequested(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    reviewer: ProposalReviewer,
  ) {
    event.stopPropagation();
    onRequestReReview?.(reviewer);
  }

  function handleMessagePopoverOpened(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    reviewer?: ProposalReviewer,
  ) {
    const selected = selectedReviewers.find((r) => r.userId.isEqualTo(reviewer?.userId));
    setCurrentReviewerEditing(selected ?? reviewer);
    setPopoverAnchorElement(event?.currentTarget);
  }

  function handleReviewerMessageChanged(userId?: Guid, message?: string) {
    setPopoverAnchorElement(null);

    if (!userId || !selectedReviewers) return;

    // Update the custom message for the reviewer
    const updatedReviewers = [...selectedReviewers].map((r) => {
      if (r.userId.isEqualTo(userId)) {
        return new ProposalReviewer({
          entityMember: r.entityMember,
          isEditing: r.isEditing,
          dateApproved: r.dateApproved,
          customMessage: message,
        });
      }
      return r;
    });

    setStagedReviewerSelection(updatedReviewers);
    onReviewersChanged?.(updatedReviewers);
  }

  const selectedReviewersByUserId = useMemo(
    () => new Map(selectedReviewers.map((reviewer) => [reviewer.userId.value, reviewer])),
    [selectedReviewers],
  );
  const addedReviewerUserIds = useMemo(() => {
    const originalReviewerUserIds = new Set(originalReviewers.map((reviewer) => reviewer.userId.value));
    return new Set(
      selectedReviewers
        .filter((reviewer) => !originalReviewerUserIds.has(reviewer.userId.value))
        .map((reviewer) => reviewer.userId.value),
    );
  }, [originalReviewers, selectedReviewers]);
  const reviewerPoolCoworkersByUserId = useMemo(
    () => new Map(reviewerDisplayCoworkers.map((coworker) => [coworker.userId.value, coworker])),
    [reviewerDisplayCoworkers],
  );
  const reviewerIndividualsByUserId = useMemo(
    () => new Map(reviewerIndividuals.map((individual) => [individual.userId.value, individual])),
    [reviewerIndividuals],
  );
  const reviewerResults = useMemo(
    () =>
      filterReviewerPool(
        reviewerPool ?? [],
        reviewerPoolCoworkers,
        searchTerm === "" ? "" : debouncedSearchTerm,
      ),
    [debouncedSearchTerm, reviewerPool, reviewerPoolCoworkers, searchTerm],
  );
  // Active first (#5134). The rows are the coworker directory with the roster folded into it, so the
  // people actually on the proposal would otherwise sit wherever the directory's alphabet put them.
  const visibleReviewers = useMemo(
    () =>
      orderReviewersForDisplay({
        reviewers: mergeReviewersByUserId(reviewerResults, selectedReviewers, originalReviewers),
        activeReviewerUserIds: new Set(selectedReviewers.map((reviewer) => reviewer.userId.value)),
      }),
    [originalReviewers, reviewerResults, selectedReviewers],
  );
  const sendActionState = getReviewerSendActionState({
    originalReviewers,
    selectedReviewers,
    proposalHasUnsavedChanges,
  });

  return (
    <ReviewerPopover
      anchorEl={popoverAnchor}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      marginThreshold={isMobileDisplay ? 8 : 16}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={popoverAnchor !== undefined}
      onClose={handlePopoverClosed}
    >
      <PopoverContainer>
        <SearchField
          placeholder="Search for reviewer"
          size="small"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {searchTerm !== "" && (
                    <IconButton
                      size="small"
                      aria-label="Clear reviewer search"
                      onClick={() => {
                        setSearchTerm("");
                        search("");
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            },
          }}
          value={searchTerm}
          onChange={(event) => search(event.target.value)}
        />
        {addedReviewerUserIds.size > 0 && (
          <ReviewerNotice severity="info">Save the proposal to notify newly added reviewers.</ReviewerNotice>
        )}
        {isInitialLoading && <LoaderProgress size={50} disableShrink thickness={4} />}
        {!isInitialLoading && (
          <ListContainer dense ref={reviewerListRef}>
            {visibleReviewers.length < 1 && (
              <ListItem>
                <ListItemText>No Reviewers Found</ListItemText>
              </ListItem>
            )}
            {visibleReviewers.length > 0 &&
              visibleReviewers.map((reviewer: ProposalReviewer) => {
                const selectedReviewer = selectedReviewersByUserId.get(reviewer.userId.value);
                const isSelected = Boolean(selectedReviewer);
                const isEditor = selectedReviewer?.isEditing === true;
                const hasApproved = Boolean(selectedReviewer?.dateApproved);
                const customMessage = selectedReviewer?.customMessage?.trim() ?? "";
                const hasCustomMessage = Boolean(customMessage.length);
                const isNewlyAdded = addedReviewerUserIds.has(reviewer.userId.value);
                const coworker = reviewerPoolCoworkersByUserId.get(reviewer.entityMember.userId.value);
                const individual = coworker?.asIndividual() ?? reviewerIndividualsByUserId.get(reviewer.userId.value);
                const reviewerName = coworker?.name.fullName ?? individual?.fullName ?? "Unknown reviewer";
                const reviewerStatus = hasApproved ? "Reviewed" : coworker ? "Co-worker" : "Reviewer";
                const isReReviewPending = reReviewPendingUserIds?.has(reviewer.userId.value) === true;

                return (
                  <ReviewerListItem key={reviewer.userId.value} selected={isSelected}>
                    <ListItemButton selected={isSelected} onClick={(event) => handleReviewerSelected(event, reviewer)}>
                      <ReviewerAvatar>
                        {isSelected && (
                          <SelectedIcon>
                            <DoneIcon />
                          </SelectedIcon>
                        )}
                        <IndividualAvatar individual={individual} />
                      </ReviewerAvatar>
                      <ReviewerListItemText
                        selected={isSelected}
                        primary={reviewerName}
                        secondary={
                          <ReviewerSecondary>
                            <ReviewerStatus approved={hasApproved}>{reviewerStatus}</ReviewerStatus>
                            {hasCustomMessage && (
                              <Tooltip
                                enterDelay={600}
                                placement="bottom"
                                title={
                                  <ReviewerMessageTooltipTitle>Message: {customMessage}</ReviewerMessageTooltipTitle>
                                }
                              >
                                <ReviewerMessage>Message: {customMessage}</ReviewerMessage>
                              </Tooltip>
                            )}
                          </ReviewerSecondary>
                        }
                      />
                    </ListItemButton>
                    <ActionsContainer>
                      <EditingSwitch
                        label="Allow Editing"
                        labelPlacement="bottom"
                        control={
                          <Switch
                            checked={isEditor}
                            color="primary"
                            onChange={(event) => handleEditToggled(event, reviewer)}
                            onClick={(event) => event.stopPropagation()}
                            size="small"
                          />
                        }
                      />
                      <ReviewerIconAction>
                        {isSelected && !isNewlyAdded && hasApproved && canRequestReReview && (
                          // A spinner rather than a disabled button while the request is out: a
                          // disabled control swallows the hover that would explain why it is dead,
                          // which is the whole of what the "requesting" caption used to say.
                          isReReviewPending ? (
                            <CircularProgress aria-label={`Requesting re-review from ${reviewerName}`} size={18} />
                          ) : (
                            <Tooltip title="Request re-review">
                              <IconButton
                                size="small"
                                aria-label={`Request re-review from ${reviewerName}`}
                                onClick={(event) => handleReReviewRequested(event, reviewer)}
                              >
                                <ReplayIcon color="secondary" fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )
                        )}
                        {isSelected && isNewlyAdded && (
                          <Tooltip title={`${hasCustomMessage ? "Edit" : "Add"} Custom Message`}>
                            <IconButton
                              size="small"
                              aria-label={`${hasCustomMessage ? "Edit" : "Add"} Custom Message`}
                              onClick={(event) => handleMessagePopoverOpened(event, reviewer)}
                            >
                              {hasCustomMessage ? (
                                <CommentIcon color="secondary" fontSize="small" />
                              ) : (
                                <AddCommentIcon color="secondary" fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </ReviewerIconAction>
                      {/*
                        #5134. Clicking the row already toggles a reviewer off, but a row that both
                        opens and closes reads as a selector, not as a roster — so the removal an
                        actor came here to make has its own named control, and one that says what it
                        does to a screen reader rather than "Alice Reviewer".
                      */}
                      <ReviewerIconAction>
                        {isSelected && (
                          <Tooltip title={`Remove ${reviewerName} as a reviewer`}>
                            <ProposalDeleteActionButton
                              size="small"
                              aria-label={`Remove ${reviewerName} as a reviewer`}
                              onClick={(event) => handleReviewerRemoved(event, reviewer)}
                            />
                          </Tooltip>
                        )}
                      </ReviewerIconAction>
                    </ActionsContainer>
                  </ReviewerListItem>
                );
              })}
            {isLoading && coworkers.data.length > 0 && (
              <ReviewerLoadingListItem>
                <CircularProgress aria-label="Loading more reviewers" disableShrink size={24} thickness={4} />
              </ReviewerLoadingListItem>
            )}
            {coworkerError && (
              <ListItem>
                <ReviewerLoadError
                  action={
                    <Button color="inherit" size="small" onClick={retryCoworkers}>
                      Retry
                    </Button>
                  }
                  severity="error"
                >
                  Failed to load reviewers.
                </ReviewerLoadError>
              </ListItem>
            )}
          </ListContainer>
        )}
        {onSendToReviewers && (
          <SendActionContainer>
            {/*
              Genuinely `disabled`, not merely greyed, so assistive technology announces it as
              unavailable — and described by the visible caption below it, which says which half of the
              rule is unmet rather than leaving the reader to guess.
            */}
            <LoadingButton
              variant="contained"
              color="primary"
              size="small"
              startIcon={<SendIcon />}
              loading={isSendingToReviewers}
              disabled={sendActionState.disabled}
              aria-describedby={sendActionDescriptionId}
              onClick={() => onSendToReviewers({ confirmSave: sendActionState.confirmsUnsavedProposalChanges })}
            >
              {sendActionState.label}
            </LoadingButton>
            <SendActionDescription id={sendActionDescriptionId} variant="caption">
              {sendActionState.description}
            </SendActionDescription>
          </SendActionContainer>
        )}
      </PopoverContainer>
      {/*
        Mounted per opening, and keyed by the reviewer it belongs to, so the editor seeds itself from
        THAT reviewer's saved message. Kept mounted across openings, it showed the first reviewer's
        draft for every reviewer opened after them.
      */}
      {popoverAnchorElement && (
        <ReviewerMessagePopover
          key={currentReviewerEditing?.userId?.value ?? "no-reviewer"}
          userId={currentReviewerEditing?.userId ?? undefined}
          anchorEl={popoverAnchorElement}
          message={currentReviewerEditing?.customMessage}
          onCanceled={() => setPopoverAnchorElement(null)}
          onMessageChanged={(userId?: Guid, message?: string) => {
            handleReviewerMessageChanged(userId, message);
          }}
        />
      )}
    </ReviewerPopover>
  );
}
